import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import * as Yup from 'yup';
import { FormControl, Box } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { v4 } from 'uuid';
import { FormBodyAPI, SetFieldHandler } from 'src/constants/fieldsTypes';
import { RootState } from 'src/store';
import BaseTypography from 'src/components/Text/BaseTypography';
import { BaseTextField } from 'src/components/TextField';

const validationScheme = Yup.object().shape({
  receiverUserId: Yup.string().max(255).required('Client is required'),
});

const getSubmitValues = async () => ({
  key: v4(),
});

interface ClientFields {
  clientName: string;
  receiverUserId: string;
}

export const useRecipientNameForm = () => {
  const [clientsOptions, setClientsOptions] = useState<Array<ClientFields>>([]);
  const [currentClient, setCurrentClient] = useState<ClientFields | null>({
    clientName: '',
    receiverUserId: '',
  });

  const { activeClients, selectedChannel } = useSelector(
    (state: RootState) => ({
      activeClients: state.clients.activeClients,
      selectedChannel: state.files.selectedChannel,
    }),
    shallowEqual,
  );

  const handleClientChange = (
    _: React.ChangeEvent<any>,
    selectedClient?: any,
    setFieldValue?: SetFieldHandler,
  ) => {
    if (selectedClient && setFieldValue) {
      setCurrentClient(selectedClient);
      setFieldValue('receiverUserId', selectedClient.receiverUserId);
    } else if (!selectedClient && setFieldValue) {
      setCurrentClient(null);
      setFieldValue('receiverUserId', '');
    }
  };

  useEffect(() => {
    if (selectedChannel?.additionalFields) {
      const clientsList = activeClients;
      /**
       * Map channelMemberIDs from object pair to an array of string
       */
      const channelMemberIDs = selectedChannel.additionalFields?.memberIDs
        ? Object.keys(selectedChannel.additionalFields?.memberIDs).map(
            (key) => key,
          )
        : [];

      const clients = clientsList
        .filter((c) => channelMemberIDs?.includes(c.id))
        .map((client) => ({
          clientName: `${client.fields.given_name} ${client.fields.family_name}`,
          receiverUserId: client.id,
        }));

      setClientsOptions(clients);
    }
  }, [selectedChannel]);

  const FormRenderer: React.FC<FormBodyAPI> = ({
    touched,
    errors,
    handleBlur,
    values,
    setFieldValue,
  }) => (
    <Box>
      <FormControl fullWidth>
        <BaseTypography fontType="13Medium">Recipient</BaseTypography>
        <Autocomplete
          freeSolo
          defaultValue={{
            clientName: values ? values.clientName : '',
            receiverUserId: values ? values.receiverUserId : '',
          }}
          value={currentClient}
          onChange={(event, value) =>
            handleClientChange(event, value, setFieldValue)
          }
          options={clientsOptions}
          getOptionLabel={(option) => option.clientName}
          renderInput={(params) => (
            <BaseTextField
              sizeVariant="medium"
              {...params}
              error={
                touched && errors
                  ? Boolean(touched.receiverUserId && errors.receiverUserId)
                  : false
              }
              helperText={
                touched && errors
                  ? touched.receiverUserId && errors.receiverUserId
                  : ''
              }
              value={values ? values.clientName : ''}
              name="receiverUserId"
              onChange={(event) => handleClientChange(event)}
              fullWidth
              onBlur={handleBlur}
              autoComplete="new-password"
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                isautocomplete: 'true',
              }}
            />
          )}
        />
      </FormControl>
    </Box>
  );

  return { validationScheme, getSubmitValues, FormRenderer };
};
